import React, { useContext, useState, useEffect, useMemo } from 'react';
import t from '@jetshop/intl';
import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import AddToCartWrapper from './AddToCart/AddToCartWrapper';
import { Helmet } from 'react-helmet-async';
import ImageContainerNew from './Images/ImageContainerNew';
import { styled } from 'linaria/react';
import { theme } from '../Theme';
import ProductPageLoadingState from './ProductPageLoadingState';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import { default as Breadcrumbs } from '@jetshop/ui/Breadcrumbs';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import printer from '../../svg/PrinterNew.svg';
import { Above, Below } from '@jetshop/ui/Breakpoints';
import { ProductContainer, SubHeading } from './styledComponents';
import { UpsellNew } from './UpsellNew';
import ProductInfoAccordion from './ProductInfoAccordion';
import { PriceComponent } from './PriceComponent';
import AddToCartButton from './AddToCart/AddToCartButton';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import uspQuery from './UspQuery.gql';
import { useQuery } from '@apollo/react-hooks';
import { VariantSelector } from './VariantSelector';
import { Campaigns } from './Campaigns';
import ConfiguratorLink, {
  LinkStudio3D,
} from '../Configurator/ConfiguratorLink';
import StockStatusIndicator from './StockStatus/StockStatusIndicator';
import check from '../../svg/Check-icon.svg';
import { StoreContext } from '../StoreHandler/StoreContext';
import { RelatedProducts } from './RelatedProducts';
import NotifyWhenBack from './StockStatus/NotifyWhenBack';
import { PackageProduct } from './PackageProduct/PackageProduct';
import { PackageProductNew } from './PackageProduct/PackageProductNew';
import { useLipscore } from '../../hooks/useLipscore';

const InStoreInfo = styled('div')`
  padding: 0.5rem 1rem;
  margin-top: 1rem;
  width: 100%;
  height: 50px;
  font-size: 14px;
  font-weight: 700;
  font-family: Lato;
  color: #ffffff;
  background: #27ae60;
  border: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  text-transform: uppercase;
  border-radius: 50px;
  cursor: pointer;
`;

const TopWrapper = styled('div')`
  display: flex;
  width: 100%;
  ${theme.below.lg} {
    flex-direction: column;
  }
`;

const BottomWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 60px;
  ${theme.below.md} {
    margin-top: 20px;
  }
`;

const GalleryWrapper = styled('div')`
  width: 100%;
  max-width: calc(100% - 540px);
  ${theme.below.lg} {
    max-width: 100%;
  }
`;

const ProductInformationSection = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 540px;
  padding: 60px 80px;

  .block {
    :after {
      margin-top: 20px;
      margin-bottom: 28px;
      content: '';
      display: block;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    }
  }

  ${theme.below.lg} {
    max-width: 100%;
    padding: 20px 12px;
  }
`;

const Printer = styled('img')`
  height: 25px;
  width: 25px;
  display: block;
  position: absolute;
  right: 0;
  top: 0%;
`;

const ProductInformation = styled('div')`
  .name-row {
    display: flex;
    justify-content: space-between;
  }

  .lipscore-wrapper {
    margin-top: 2px;
    cursor: pointer;
    display: block;

    &:hover {
      opacity: 0.8;
    }

    .lipscore-rating-review-link {
      color: black;
      margin-left: 5px;
      display: none;
    }

    #lipscore-rating {
      display: flex;
      align-items: center;
      pointer-events: none;

      .lipscore-rating-star {
        height: 20px;
        width: 20px;
      }
    }

    /* Base styling for all rating stars */
    .lipscore-rating-star,
    .lipscore-rating-star-wrapper .lipscore-rating-star {
      fill: #cccccc; /* Default inactive star color */
    }

    /* Active/complete stars - make them #FEB846 */
    .lipscore-rating-star:not(.lipscore-star-incomplete):not(
        .lipscore-rating-star-inactive
      ) {
      fill: #feb846 !important;
    }

    /* For partial/incomplete stars */
    .lipscore-rating-star.lipscore-star-incomplete {
      fill: #cccccc; /* Base color for incomplete stars */
    }

    /* Target the filled portion of incomplete stars */
    .lipscore-rating-star.lipscore-star-incomplete > g:first-child,
    .lipscore-rating-star.lipscore-star-incomplete > g:first-child > path,
    .lipscore-rating-star.lipscore-star-incomplete .lipscore-star-first-half {
      fill: #feb846 !important;
    }

    /* Target the unfilled portion of incomplete stars */
    .lipscore-rating-star.lipscore-star-incomplete > g:last-child,
    .lipscore-rating-star.lipscore-star-incomplete > g:last-child > path,
    .lipscore-rating-star.lipscore-star-incomplete .lipscore-star-last-half {
      fill: #cccccc !important;
    }

    /* Alternative targeting for Lipscore's different star structures */
    .lipscore-rating-star.lipscore-star-incomplete defs + g {
      fill: #feb846 !important;
    }

    .lipscore-rating-star.lipscore-star-incomplete g + g {
      fill: #cccccc !important;
    }

    .lipscore-rating-value {
      font-weight: 400;
      font-size: 14px;
    }

    .lipscore-compact-rating-wrapper {
      display: flex;
      align-items: center;

      .lipscore-compact-rating-value {
        font-family: ${theme.fonts.primary};
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 14px;
        display: flex;
        align-items: center;
        letter-spacing: -0.055em;
        margin-left: 3px;
      }

      .lipscore-compact-rating-votes {
        opacity: 0.7;
        font-family: ${theme.fonts.primary};
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 14px;
        display: flex;
        align-items: center;
        letter-spacing: -0.055em;
      }
    }
  }

  .print-wrapper {
    position: relative;
    width: 20%;
  }

  .sub-heading {
    margin: 0;
  }
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
  margin-top: 0 !important;
  margin-bottom: 0;
  padding: 0;
  min-height: 19px;
  overflow: hidden;

  a,
  li {
    color: #494949 !important;
    font-family: ${theme.fonts.primary};
    font-size: 12px !important;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-decoration: none;
    white-space: normal;
  }

  li:last-of-type {
    ::after {
      display: none;
    }
  }

  ${theme.above.md} {
    margin-top: 1.5rem;

    a,
    li {
      font-size: 12px;
    }
  }
`;

const Heading = styled('h1')`
  color: #000;
  font-family: ${theme.fonts.primary};
  font-size: 22px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-transform: uppercase;
  margin: 0;
`;

const ShortDescription = styled('article')`
  margin-top: 20px;
  color: #000;
  font-family: ${theme.fonts.primary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%; /* 22.4px */
`;

const ReadMore = styled('span')`
  margin-bottom: 20px;
  font-size: 14px;
  cursor: pointer;
  margin-top: 10px;
  text-decoration: underline;
`;

const ProductInfoAccordionWrapper = styled('div')`
  width: 100%;
  background: rgba(246, 242, 234, 0.6);
  padding: 50px 0;
`;

const InnerProductInfoAccordionWrapper = styled('div')`
  background: #fff;
  max-width: 720px;
  margin: auto;
`;

const PriceWrapper = styled('div')`
  .price-row {
    display: flex;
    justify-content: space-between;
  }
`;

const UspBarContainer = styled('div')`
  margin-top: 12px;
  p {
    margin-top: 0;
    margin-bottom: 8px;
    font-size: 14px;
    line-height: 14px;
    font-weight: 400;
    letter-spacing: 0.2px;
  }

  p::before {
    display: inline-block;
    content: url(${check});
    background-color: white;
    position: relative;
    width: 15px;
    margin-right: 8px;
  }
`;

function getBrand(product) {
  const field = product?.customFields?.find(({ key }) => key === 'brand');
  if (field) {
    return field.stringValue;
  }
}

const isConfigurationProduct = (templateData) => {
  const templateItem = templateData.find((item) => item.key === 'configurator');
  return templateItem ? templateItem.boolValue : false;
};

function getField(product, fieldName) {
  const field = product?.customFields?.find(({ key }) => key === fieldName);
  if (field) {
    return field.stringValue || field.listValues;
  }
  return null;
}

const ProductMeta = ({ product, selectedVariation }) => {
  const { selectedChannel } = useContext(ChannelContext);
  const prod = selectedVariation || product;
  const brand = getBrand(product);

  return (
    <Helmet>
      <meta property="og:title" content={product.name} />
      <meta
        property="product:original_price:amount"
        content={prod?.price?.incVat}
      />
      <meta
        property="product:price:amount"
        content={prod?.previousPrice?.incVat}
      />
      <meta
        property="og:price:currency"
        content={selectedChannel.currency.id}
      />
      {product?.images?.length > 0 && (
        <meta property="og:image" content={product.images[0].url} />
      )}
      <meta
        property="product:availability"
        content={prod.stockStatus.buyable ? 'instock' : 'oos'}
      />
      {brand && <meta property="product:brand" content={brand} />}
      {product.canonicalCategory && (
        <meta
          property="product:category"
          content={product.canonicalCategory.name}
        />
      )}
      <meta
        property="og:url"
        content={`${selectedChannel.url}${product.primaryRoute.path}`}
      />
    </Helmet>
  );
};

const StoreProductStatus = ({ storeName, setContactOpen }) => {
  const { storeList } = useContext(StoreContext);
  let email = false;
  if (storeList) {
    const currentStore = storeList.find((store) => {
      return storeName === store.name;
    });
    email = currentStore?.storeInfo?.email;
  }

  return (
    <>
      <InStoreInfo
        onClick={() => {
          setContactOpen(true);
          document.getElementById('contact').scrollIntoView({
            block: 'center',
            behavior: 'smooth',
          });
        }}
      >{`Kontakta möbelmästarna i ${storeName}`}</InStoreInfo>
    </>
  );
};

const Product = ({ result: { data, loading }, product }) => {
  useLipscore();
  const { selectedChannel } = useContext(ChannelContext);
  const { selectedStore } = useContext(StoreContext);
  const urlVariant = useVariantFromUrl();
  const productVideoUrl = product?.customFields?.find(
    ({ key }) => key === 'product-video-url'
  )?.stringValue;
  const productVideoAspect = product?.customFields?.find(
    ({ key }) => key === 'product-video-aspect-ratio'
  )?.stringValue;

  let initialVariant = urlVariant?.values ? urlVariant : { values: [] };

  if (product?.hasVariants && !initialVariant.values.length) {
    const defaultValues = [];
    product?.variants?.options?.forEach(({ values }) => {
      defaultValues.push(values[0]);
    });
    initialVariant = { values: defaultValues };
  }

  let images = product?.images?.length > 0 ? [...product?.images] : [];
  if (productVideoUrl) {
    images.unshift({
      type: 'video',
      videoSrc: productVideoUrl,
      aspect: productVideoAspect,
    });
  }

  const parents = getParentOrCanonicalRoute(data?.route?.parents, product);

  const storeList = getField(product, 'store_list') || [];
  const hidePrice = product?.hidePrice;

  const [contactOpen, setContactOpen] = useState(false);
  const [rating, setRating] = useState(null);
  const [reviewCount, setReviewCount] = useState(null);

  const show3DStudioLink = product?.customFields?.find(
    ({ key }) => key === 'link-studio-3d'
  )?.boolValue;

  const storeBadge = useMemo(() => {
    if (
      !selectedStore?.name ||
      !product?.subName ||
      selectedStore.name !== storeName
    )
      return null;

    return {
      name: 'store',
      location: 'TOP_LEFT',
      style: 'store-badge',
      text: selectedStore.name,
    };
  }, [selectedStore?.name, product?.subName, product?.customFields]);

  const storeName = storeList?.find((store) => store === selectedStore?.name);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const getRating = () => {
      const ratingElement = document.querySelector(
        '#lipscore-rating .lipscore-rating-value'
      );
      const reviewCountElement = document.querySelector(
        '#lipscore-rating .lipscore-rating-votes'
      );

      if (ratingElement?.textContent && reviewCountElement?.textContent) {
        const ratingValue = ratingElement.textContent.trim();
        const countValue = reviewCountElement.textContent.trim();

        setRating(ratingValue);
        setReviewCount(countValue);
        return true;
      }
      return false;
    };

    // Try to get rating immediately if element exists
    if (!getRating()) {
      // If not found, set up observer and retry mechanism
      const observer = new MutationObserver((mutations, obs) => {
        if (getRating()) {
          obs.disconnect(); // Disconnect once we get the rating
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      // Also set up a retry mechanism
      const retryInterval = setInterval(() => {
        if (getRating()) {
          clearInterval(retryInterval);
        }
      }, 1000); // Check every second

      // Cleanup
      return () => {
        observer.disconnect();
        clearInterval(retryInterval);
      };
    }
  }, []);

  const variantHandler = useProductVariants(
    product?.articleNumber ? product : undefined,
    { initialVariant }
  );

  const { selectedVariant: selectedVariation } = variantHandler;

  const stockStatus = useStockStatus(selectedVariation || product);
  const uspResult = useQuery(uspQuery, {
    variables: {
      id: 255,
    },
  });

  let uspContent = uspResult?.data?.category?.content;

  if (!product)
    return (
      <ProductContainer>
        <ProductPageLoadingState />
      </ProductContainer>
    );

  const { relatedProducts, isPackage, distributionPackageSize } = product;

  const upsellProducts = product?.customFields?.find(
    ({ key }) => key === 'Upsell'
  )?.listValues;

  const isConfigurable = isConfigurationProduct(product?.customFields);
  const hasNewConfigurator = product?.customFields?.find(
    ({ key }) => key === 'newConfigurator'
  )?.boolValue;

  const packageProduct = product?.customFields?.find(
    ({ key }) => key === 'paket'
  );

  // Add a print handler function
  const handlePrint = (e) => {
    e.preventDefault();
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  const outOfStock = stockStatus?.status === 'outOfStock';

  return (
    <AddToCartWrapper product={product} variantHandler={variantHandler}>
      {({ errors }) => (
        <ProductContainer>
          <TopWrapper>
            <GalleryWrapper>
              <ImageContainerNew images={images} variant={selectedVariation} />
            </GalleryWrapper>
            <ProductInformationSection>
              <ProductMeta {...{ product, selectedVariation }} />
              <div className="hideInPrint">
                {parents && product?.breadcrumbText && (
                  <StyledBreadcrumbs
                    parents={parents}
                    breadcrumbText={product?.breadcrumbText}
                  />
                )}
              </div>
              <ProductInformation className="block">
                <div className="name-row">
                  <Heading data-testid="product-title">
                    {product.name || 'Loading...'}
                  </Heading>
                  <div className="print-wrapper">
                    <a className="hideInPrint" href="#" onClick={handlePrint}>
                      <Printer src={printer} alt="Print this page" />
                    </a>
                  </div>
                </div>

                <SubHeading className="sub-heading">
                  {storeName || product?.subName}
                </SubHeading>

                <div className="hideInPrint">
                  <button
                    className="lipscore-wrapper"
                    onClick={() => {
                      const reviewsSection = document.querySelector('#reviews');
                      const reviewsHeading =
                        document.querySelector('[data-section="2"]');

                      if (reviewsHeading && reviewsSection) {
                        reviewsHeading.click();
                        setTimeout(() => {
                          reviewsSection.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                          });
                        }, 300);
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      width: 'fit-content',
                    }}
                  >
                    <div
                      id="lipscore-rating"
                      ls-product-name={product?.name}
                      ls-brand={product?.subName}
                      ls-product-id={product?.id}
                      ls-product-url={
                        selectedChannel?.url + product?.primaryRoute?.path
                      }
                      ls-description={product?.shortDescription}
                      ls-price={product?.price?.incVat}
                      ls-price-currency={selectedChannel?.currency?.name}
                    />
                  </button>
                </div>
                <Above breakpoint="md">
                  <>
                    <ShortDescription
                      dangerouslySetInnerHTML={{
                        __html: product?.shortDescription,
                      }}
                    />
                    {product?.description && (
                      <ReadMore
                        onClick={() => {
                          document
                            .getElementById('description')
                            .scrollIntoView({
                              block: 'center',
                              behavior: 'smooth',
                            });
                        }}
                      >
                        {t(' Read more')}
                      </ReadMore>
                    )}
                  </>
                </Above>
              </ProductInformation>
              <PriceWrapper className="block">
                <div className="price-row">
                  <PriceComponent
                    product={product}
                    selectedVariation={selectedVariation}
                    isPackage={isPackage}
                    packageProduct={packageProduct}
                  />
                  {/* {!hidePrice && (
                    <PriceHistoryWrapper
                      articleNumber={product.articleNumber}
                      variant={selectedVariation}
                    />
                  )} */}
                </div>
                {product?.campaigns && (
                  <Campaigns
                    campaigns={product?.campaigns}
                    css={{ marginTop: '-16px', marginBottom: '24px' }}
                  />
                )}

                <div className="hideInPrint">
                  {packageProduct && (
                    <PackageProductNew
                      product={product}
                      selectedvariation={selectedVariation}
                    />
                  )}

                  {isPackage && (
                    <PackageProduct
                      product={product}
                      selectedvariation={selectedVariation}
                    />
                  )}
                </div>
              </PriceWrapper>

              <div>
                {show3DStudioLink && <LinkStudio3D />}
                {isConfigurable && <ConfiguratorLink />}
                {hasNewConfigurator && <ConfiguratorLink newConfig={true} />}
                {product?.variants && product?.variants?.options ? (
                  <>
                    <VariantSelector
                      product={product}
                      variantHandler={variantHandler}
                      selectedVariation={selectedVariation}
                    />
                  </>
                ) : null}
                {storeName && !hidePrice && outOfStock ? (
                  <div className="hideInPrint">
                    <StoreProductStatus
                      storeName={storeName}
                      product={product}
                      contactOpen={contactOpen}
                      setContactOpen={setContactOpen}
                    />
                  </div>
                ) : stockStatus?.status === 'notifyWhenBack' ? (
                  <div className="hideInPrint">
                    <NotifyWhenBack
                      text={stockStatus?.text}
                      articleNumber={
                        (selectedVariation || product)?.articleNumber
                      }
                    />
                  </div>
                ) : (
                  !isPackage && (
                    <div className="hideInPrint">
                      <AddToCartButton
                        inStock={stockStatus?.status === 'inStock'}
                        variantHandler={variantHandler}
                        distributionPackageSize={distributionPackageSize}
                        errors={errors}
                      />
                    </div>
                  )
                )}
                <div className="hideInPrint">
                  <StockStatusIndicator
                    status={stockStatus?.status}
                    text={stockStatus?.text}
                    storeName={storeName}
                  />
                  {uspContent && (
                    <UspBarContainer
                      dangerouslySetInnerHTML={{ __html: uspContent }}
                    />
                  )}
                  {/* On behalf of customer, reinstate shortdescription in mobile (2024.07.17) */}
                  <Below breakpoint="md">
                    <>
                      <div style={{ height: '20px' }}></div>
                      <h2
                        style={{
                          fontSize: '16px',
                          fontWeight: '400',
                          fontStyle: 'normal',
                          marginBottom: '0',
                        }}
                      >
                        {t('Short product description')}
                      </h2>
                      <ShortDescription
                        dangerouslySetInnerHTML={{
                          __html: product?.shortDescription,
                        }}
                      />
                      {product?.description && (
                        <ReadMore
                          onClick={() => {
                            document
                              .getElementById('description')
                              .scrollIntoView({
                                block: 'start',
                                behavior: 'smooth',
                              });
                          }}
                        >
                          {t(' Read more')}
                        </ReadMore>
                      )}
                    </>
                  </Below>
                </div>
              </div>
            </ProductInformationSection>
          </TopWrapper>
          <BottomWrapper>
            {upsellProducts?.length > 0 && (
              <>
                <UpsellNew productIds={upsellProducts} />
              </>
            )}
            <ProductInfoAccordionWrapper>
              <InnerProductInfoAccordionWrapper>
                <ProductInfoAccordion
                  product={product}
                  storeName={storeName}
                  contactOpen={contactOpen}
                  setContactOpen={setContactOpen}
                  rating={rating}
                  reviewCount={reviewCount}
                />
              </InnerProductInfoAccordionWrapper>
            </ProductInfoAccordionWrapper>

            {relatedProducts && relatedProducts?.length > 0 && (
              <RelatedProducts products={relatedProducts} />
            )}
          </BottomWrapper>
        </ProductContainer>
      )}
    </AddToCartWrapper>
  );
};

export default Product;
